import { extractStatusCode } from '~/utils/extractStatusCodeFromError';
import log from '~/utils/log';

export type ConsentPollingState = 'pending' | 'success' | 'timeout' | 'error';

/** Roughly half the attempt budget, after which the UI mentions that it is taking longer. */
const SLOW_HINT_AFTER_ATTEMPTS = 12;

/** Consecutive, so a single failed request does not end the wait. */
const MAX_CONSECUTIVE_ERRORS = 3;

/**
 * A request that never settles would stop the poll from scheduling the next check. Sized above the
 * endpoint's own budget of two Docebo calls at `DOCEBO_API_TIMEOUT` each.
 */
const REQUEST_TIMEOUT_MS = 25_000;

/**
 * Polls Docebo for the user's account status while the asynchronous consent pipeline runs.
 *
 * Uses `useTimeoutPoll` rather than `useIntervalFn` because it schedules the next request only once
 * the previous one has settled, so a slow response cannot pile up in-flight requests.
 */
export const useDoceboConsentPolling = () => {
    const runtimeConfig = useRuntimeConfig();
    const { intervalMs, maxAttempts } = runtimeConfig.public.academy.consentPolling;

    const state = ref<ConsentPollingState>('pending');
    const attempts = ref(0);
    const consecutiveErrors = ref(0);

    const isTakingLong = computed(() => state.value === 'pending' && attempts.value >= SLOW_HINT_AFTER_ATTEMPTS);

    /** Must not throw: an unhandled rejection inside `useTimeoutPoll` stops the schedule silently. */
    const check = async () => {
        if (attempts.value >= maxAttempts) {
            pause();
            state.value = 'timeout';
            return;
        }

        attempts.value++;

        try {
            const { status } = await $fetch<{ status: 'active' | 'inactive' }>('/api/academy/docebo/user-status', {
                timeout: REQUEST_TIMEOUT_MS
            });
            consecutiveErrors.value = 0;

            if (status === 'active') {
                pause();
                state.value = 'success';
            }
        } catch (error) {
            log.error('[useDoceboConsentPolling] Failed to fetch the Docebo user status', error);

            /** The session expired while waiting; polling cannot recover from that. */
            if (extractStatusCode(error) === 401) {
                pause();
                state.value = 'error';
                return;
            }

            consecutiveErrors.value++;
            if (consecutiveErrors.value >= MAX_CONSECUTIVE_ERRORS) {
                pause();
                state.value = 'error';
            }
        }
    };

    /**
     * The scheduled poll, the initial check and the visibility handler can all trigger a request.
     * `useTimeoutPoll` only serializes its own loop, and `pause()` does not cancel a request that is
     * already in flight, so requests are funnelled through here to keep at most one running.
     */
    let inFlight: Promise<void> | null = null;
    const runCheck = () => {
        inFlight ??= check().finally(() => {
            inFlight = null;
        });

        return inFlight;
    };

    const { pause, resume } = useTimeoutPoll(runCheck, intervalMs, { immediate: false });

    const start = async () => {
        /** The account may already have been activated during the redirect. */
        await runCheck();

        if (state.value === 'pending') resume();
    };

    const retry = () => {
        attempts.value = 0;
        consecutiveErrors.value = 0;
        state.value = 'pending';

        return start();
    };

    onMounted(start);
    onBeforeUnmount(pause);

    /** Stop polling in a backgrounded tab. */
    const visibility = useDocumentVisibility();
    watch(visibility, (value) => {
        if (state.value !== 'pending') return;

        if (value === 'visible') {
            void start();
        } else {
            pause();
        }
    });

    return { state, attempts, isTakingLong, maxAttempts, retry };
};
